import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { Inject, Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, from } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { DataService } from '@services/data.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private dataService: DataService,
  ) {}
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const oktaUserProfile = this.authService.oktaUserProfile;
    const userProfile = this.authService.userProfile;

    // Always save the current route for refresh scenarios (even if user is authenticated)
    if (state.url !== '/login' && state.url !== '/login/callback') {
      sessionStorage.setItem('currentRoute', state.url);
    }

    return from(this.resolveAccess(state.url));
  }

  /** Decide access, hydrating the per-tab profile first when Okta already
   *  holds a valid session. Without this, a fresh tab (or a browser
   *  restoring a deep IOP URL) loops forever: guard → /login →
   *  "already authenticated" → back to route → guard → … because only the
   *  login callback ever loaded the profile. */
  private async resolveAccess(url: string): Promise<boolean | UrlTree> {
    await this.hydrateProfileIfNeeded();

    const ok =
      !!this.authService.oktaUserProfile?.email &&
      !!this.authService.userProfile?.email;

    if (ok) {
      // Keep the shared DataService signal in lockstep with the SSO profile.
      // DataService is a root singleton that snapshots sessionStorage once in
      // its constructor; if it was created before the profile landed there
      // (a bootstrap-ordering race on fresh login / hard refresh), every screen
      // reading dataService.userProfile() would see a stale null and render
      // "zero data" until a manual refresh. Re-push the authoritative profile
      // here — the guard runs before any routed component loads, so the signal
      // is always current by the time a screen reads it.
      this.dataService.userProfile.set(this.authService.userProfile);
    }

    if (ok && this.authService.hasAccessToRoute(url)) {
      return true;
    }

    if (ok) {
      // Authenticated with a full profile but no access to THIS route —
      // send them home. Bouncing to /login here re-created the loop.
      return this.router.parseUrl('/');
    }

    // Not authenticated (or hydration failed): preserve the target and go
    // to login for a real Okta round-trip.
    if (url !== '/login' && url !== '/login/callback') {
      sessionStorage.setItem('preAuthRoute', url);
      this.oktaAuth.setOriginalUri(url);
    }
    return this.router.parseUrl('/login');
  }

  /** When Okta already holds a valid session but this tab hasn't loaded the
   *  profile yet, fetch it before the guard decides access. Failures fall
   *  through to the /login redirect rather than looping or hanging. */
  private async hydrateProfileIfNeeded(): Promise<void> {
    try {
      const needsHydration =
        !this.authService.oktaUserProfile?.email ||
        !this.authService.userProfile?.email;
      if (needsHydration && (await this.oktaAuth.isAuthenticated())) {
        if (!this.authService.oktaUserProfile?.email) {
          this.authService.oktaUserProfile = await this.oktaAuth.getUser();
        }
        if (!this.authService.userProfile?.email) {
          await this.authService.getEmployeeDetails();
        }
        const token = this.oktaAuth.getAccessToken();
        if (token) sessionStorage.setItem('token', token);
      }
    } catch (e) {
      // Hydration failed (expired session, transient BE error) — fall
      // through to the /login redirect rather than looping or hanging.
      console.error('[auth-guard] profile hydration failed:', e);
    }
  }
}
